import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Server,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Bell,
  BellOff,
  Activity,
  Clock,
  Loader2,
  Settings,
  BarChart3,
  Mail,
  AlertCircle,
  Edit2,
  Cpu,
  HardDrive,
  ShieldCheck,
  Radio,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

/**
 * Renders the rich data pushed by remote servers via the /checkin API:
 *  - per-service statuses (nginx running, postgres stopped, etc.)
 *  - host metrics (CPU/mem/disk progress bars)
 *  - SSL certificate countdown badges
 *  - last check-in timestamp
 *
 * Shows nothing if no remote check-in has happened (graceful fallback).
 */
const RemoteCheckinDetails = ({ service }) => {
  const remoteServices = service.remote_services || [];
  const metrics = service.remote_metrics || null;
  const certs = service.ssl_certificates || [];
  const lastCheckinAt = service.last_checkin_at;

  const downCount = remoteServices.filter(s => ['stopped', 'down'].includes((s.status || '').toLowerCase())).length;
  const degradedCount = remoteServices.filter(s => (s.status || '').toLowerCase() === 'degraded').length;
  const soonestCert = certs
    .filter(c => c.days_until_expiry != null)
    .sort((a, b) => a.days_until_expiry - b.days_until_expiry)[0];

  // Auto-expand if there's something the user should look at
  const hasIssue = downCount > 0 || degradedCount > 0 || (soonestCert && soonestCert.days_until_expiry < 30);
  const [open, setOpen] = useState(hasIssue);

  // No remote data at all → render nothing
  if (!lastCheckinAt && remoteServices.length === 0 && !metrics && certs.length === 0) {
    return null;
  }

  const formatRel = (iso) => {
    if (!iso) return 'never';
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  };

  const certColor = (days) => {
    if (days == null) return 'text-muted-foreground';
    if (days < 14) return 'text-red-500';
    if (days < 30) return 'text-amber-500';
    return 'text-emerald-500';
  };

  const svcColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'running': return 'text-emerald-500';
      case 'degraded': return 'text-amber-500';
      case 'stopped':
      case 'down': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/40" data-testid={`remote-checkin-${service.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`remote-checkin-toggle-${service.id}`}
      >
        <span className="flex items-center gap-2">
          <span className="shrink-0">{open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</span>
          <Radio className="w-3 h-3" />
          Remote check-in
          {downCount > 0 && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-red-500/40 text-red-500">
              {downCount} down
            </Badge>
          )}
          {degradedCount > 0 && downCount === 0 && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-500/40 text-amber-500">
              {degradedCount} degraded
            </Badge>
          )}
          {soonestCert && (
            <Badge variant="outline" className={cn("text-[10px] h-4 px-1.5", certColor(soonestCert.days_until_expiry), "border-current/40")}>
              <ShieldCheck className="w-2.5 h-2.5 mr-0.5" />
              cert {soonestCert.days_until_expiry}d
            </Badge>
          )}
        </span>
        <span className="font-mono">{formatRel(lastCheckinAt)}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`remote-checkin-details-${service.id}`}>
          {/* Host metrics */}
          {metrics && (
            <div className="grid grid-cols-3 gap-2">
              {metrics.cpu_percent != null && (
                <MetricBar icon={Cpu} label="CPU" value={metrics.cpu_percent} unit="%" thresholdAmber={70} thresholdRed={90} />
              )}
              {metrics.memory_used_gb != null && metrics.memory_total_gb && (
                <MetricBar
                  icon={Activity}
                  label="Mem"
                  value={(metrics.memory_used_gb / metrics.memory_total_gb) * 100}
                  unit="%"
                  thresholdAmber={75}
                  thresholdRed={90}
                  detail={`${metrics.memory_used_gb}/${metrics.memory_total_gb} GB`}
                />
              )}
              {metrics.disk_used_percent != null && (
                <MetricBar icon={HardDrive} label="Disk" value={metrics.disk_used_percent} unit="%" thresholdAmber={75} thresholdRed={90} />
              )}
            </div>
          )}

          {/* Per-service statuses */}
          {remoteServices.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Services</p>
              <div className="space-y-1">
                {remoteServices.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0",
                        s.status === 'running' && 'bg-emerald-500',
                        s.status === 'degraded' && 'bg-amber-500',
                        ['stopped', 'down'].includes(s.status) && 'bg-red-500',
                      )} />
                      <span className="truncate font-mono">{s.name}</span>
                      {s.version && <span className="text-muted-foreground/60">{s.version}</span>}
                    </span>
                    <span className={cn("font-medium shrink-0 ml-2", svcColor(s.status))}>
                      {s.status}
                    </span>
                  </div>
                ))}
              </div>
              {remoteServices.find(s => s.error) && (
                <div className="mt-2 text-[10px] text-red-400 space-y-0.5">
                  {remoteServices.filter(s => s.error).map((s, i) => (
                    <div key={i}>· <span className="font-mono">{s.name}</span>: {s.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SSL certificates */}
          {certs.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">SSL certificates</p>
              <div className="space-y-1">
                {certs.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="font-mono truncate">{c.domain}</span>
                    <span className={cn("font-medium ml-2", certColor(c.days_until_expiry))}>
                      {c.days_until_expiry != null ? `${c.days_until_expiry} days` : 'unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {service.last_message && (
            <p className="text-xs text-muted-foreground italic">&ldquo;{service.last_message}&rdquo;</p>
          )}
        </div>
      )}
    </div>
  );
};

const MetricBar = ({ icon: Icon, label, value, unit, thresholdAmber, thresholdRed, detail }) => {
  const pct = Math.max(0, Math.min(100, value));
  const color = pct >= thresholdRed ? 'bg-red-500' : pct >= thresholdAmber ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
        <span className="flex items-center gap-1"><Icon className="w-2.5 h-2.5" /> {label}</span>
        <span className="font-mono">{pct.toFixed(0)}{unit}</span>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full transition-all", color)} style={{ width: `${pct}%` }} />
      </div>
      {detail && <p className="text-[10px] text-muted-foreground/60 mt-0.5 font-mono">{detail}</p>}
    </div>
  );
};

export const InternalServicesSection = ({ 
  services, 
  onAddService, 
  onDeleteService, 
  onCheckService,
  onCheckAll,
  onUpdateService,
  onRefresh,
  className 
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  
  const [newService, setNewService] = useState({
    name: '',
    host: '',
    port: 443,
    check_type: 'tcp',
    alert_enabled: false,
    contact_name: '',
    contact_emails: '',
    down_message: 'The system is currently experiencing issues and will be available momentarily. We apologize for any inconvenience.',
  });

  const [editingService, setEditingService] = useState(null);

  const handleAddService = async () => {
    if (!newService.name || !newService.host) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Port is not required for ping
    const serviceData = {
      ...newService,
      port: newService.check_type === 'ping' ? 0 : parseInt(newService.port),
      contact_emails: newService.contact_emails ? newService.contact_emails.split(',').map(e => e.trim()).filter(e => e) : [],
    };
    
    await onAddService(serviceData);
    
    setAddDialogOpen(false);
    setNewService({
      name: '',
      host: '',
      port: 443,
      check_type: 'tcp',
      alert_enabled: false,
      contact_name: '',
      contact_emails: '',
      down_message: 'The system is currently experiencing issues and will be available momentarily. We apologize for any inconvenience.',
    });
    toast.success('Service added successfully');
  };

  const openEditDialog = (service) => {
    setEditingService({
      ...service,
      contact_emails: (service.contact_emails || []).join(', '),
    });
    setEditDialogOpen(true);
  };

  const handleEditService = async () => {
    if (!editingService) return;
    
    const updateData = {
      contact_name: editingService.contact_name || '',
      contact_emails: editingService.contact_emails ? editingService.contact_emails.split(',').map(e => e.trim()).filter(e => e) : [],
      down_message: editingService.down_message || '',
    };
    
    await onUpdateService(editingService.id, updateData);
    setEditDialogOpen(false);
    setEditingService(null);
    toast.success('Service updated');
    onRefresh();
  };

  const generateMailtoLink = (service) => {
    if (!service.contact_emails || service.contact_emails.length === 0) return null;
    
    const emails = Array.isArray(service.contact_emails) 
      ? service.contact_emails.join(',') 
      : service.contact_emails;
    const subject = encodeURIComponent(`[OUTAGE] ${service.name} - Service Down Notification`);
    const now = new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' });
    const body = encodeURIComponent(
      `Hello,\n\n` +
      `This is an outage notification for the following service:\n\n` +
      `Service: ${service.name}\n` +
      `Host: ${service.host}${service.port ? ':' + service.port : ''}\n` +
      `Status: DOWN\n` +
      `Detected: ${now}\n\n` +
      `${service.down_message || 'The system is currently experiencing issues and will be available momentarily. We apologize for any inconvenience.'}\n\n` +
      `We are actively investigating the issue and will provide updates as they become available.\n\n` +
      `Regards,\nIT Department`
    );
    
    return `mailto:${emails}?subject=${subject}&body=${body}`;
  };

  const handleCheckService = async (serviceId) => {
    setCheckingId(serviceId);
    await onCheckService(serviceId);
    setCheckingId(null);
  };

  const handleCheckAll = async () => {
    setIsChecking(true);
    await onCheckAll();
    setIsChecking(false);
    toast.success('All services checked');
  };

  const handleToggleAlert = async (service) => {
    await onUpdateService(service.id, {
      alert_enabled: !service.alert_enabled
    });
    toast.success(`Alerts ${service.alert_enabled ? 'disabled' : 'enabled'} for ${service.name}`);
    onRefresh();
  };

  const getStatusIcon = (status) => {
    if (status === 'up') return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    if (status === 'down') return <XCircle className="w-5 h-5 text-red-500" />;
    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'up') {
      return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">UP</Badge>;
    }
    if (status === 'down') {
      return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">DOWN</Badge>;
    }
    return <Badge variant="outline">UNKNOWN</Badge>;
  };

  const formatUptime = (percent) => {
    if (percent === undefined || percent === null) return '—';
    return `${percent.toFixed(2)}%`;
  };

  const formatResponseTime = (ms) => {
    if (ms === undefined || ms === null) return '—';
    return `${ms.toFixed(0)}ms`;
  };

  const formatLastCheck = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card className={cn('p-6 bg-card/80 backdrop-blur-sm border-border/50', className)} data-testid="internal-services-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Server className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Internal Services Monitor
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckAll}
            disabled={isChecking}
            data-testid="check-all-btn"
          >
            {isChecking ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Check All
          </Button>
          <Button
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            className="gap-2"
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {services?.map((service) => (
          <div
            key={service.id}
            data-testid={`service-card-${service.id}`}
            className={cn(
              'p-4 rounded-xl border transition-colors',
              service.status === 'up' && 'border-emerald-500/30 bg-emerald-500/5',
              service.status === 'degraded' && 'border-amber-500/40 bg-amber-500/10',
              service.status === 'down' && 'border-red-500/40 bg-red-500/10',
              (service.status === 'unknown' || !service.status) && 'border-border bg-muted/30'
            )}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(service.status)}
                <span className="font-semibold">{service.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleToggleAlert(service)}
                  title={service.alert_enabled ? 'Disable alerts' : 'Enable alerts'}
                  data-testid={`toggle-alert-${service.id}`}
                >
                  {service.alert_enabled ? (
                    <Bell className="w-4 h-4 text-primary" />
                  ) : (
                    <BellOff className="w-4 h-4 text-muted-foreground" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleCheckService(service.id)}
                  disabled={checkingId === service.id}
                  data-testid={`check-service-${service.id}`}
                >
                  {checkingId === service.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Host</span>
                <span className="font-mono">
                  {service.host}{service.check_type !== 'ping' && service.port ? `:${service.port}` : ''}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Type</span>
                <span className="uppercase">{service.check_type === 'ping' ? 'PING (ICMP)' : service.check_type}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Response</span>
                <span className="font-mono">{formatResponseTime(service.response_time_ms)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Uptime</span>
                <span className={cn(
                  'font-mono',
                  service.uptime_percent >= 99 && 'text-emerald-500',
                  service.uptime_percent < 99 && service.uptime_percent >= 95 && 'text-amber-500',
                  service.uptime_percent < 95 && 'text-red-500'
                )}>
                  {formatUptime(service.uptime_percent)}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Last Check</span>
                <span>{formatLastCheck(service.last_check)}</span>
              </div>
            </div>

            {/* Down Alert Box */}
            {service.status === 'down' && (
              <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-500">Service Unreachable</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {service.down_message || 'The system is currently experiencing issues and will be available momentarily.'}
                    </p>
                  </div>
                </div>

                {/* Contact Info */}
                {service.contact_name && (
                  <p className="text-xs text-muted-foreground mb-1">
                    Contact: <span className="font-medium text-foreground">{service.contact_name}</span>
                  </p>
                )}

                {/* Show Email Addresses */}
                {service.contact_emails && service.contact_emails.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-muted-foreground mb-1">Notify:</p>
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(service.contact_emails) ? service.contact_emails : [service.contact_emails]).map((email, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">
                          <Mail className="w-3 h-3" />
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Send Notification Button */}
                {generateMailtoLink(service) && (
                  <a 
                    href={generateMailtoLink(service)}
                    className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-md text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 transition-colors"
                    data-testid={`notify-btn-${service.id}`}
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Send Outage Notification
                  </a>
                )}
              </div>
            )}

            <RemoteCheckinDetails service={service} />
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
              <div className="flex items-center gap-2">
                {getStatusBadge(service.status)}
                {service.alert_enabled && (
                  <Badge variant="outline" className="text-xs">
                    <Bell className="w-3 h-3 mr-1" />
                    Alert
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(service)}
                  title="Edit contact settings"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onDeleteService(service.id)}
                  data-testid={`delete-service-${service.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {(!services || services.length === 0) && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Server className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No internal services configured</p>
            <p className="text-sm">Click &ldquo;Add Service&rdquo; to start monitoring</p>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {services && services.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-border/50">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Services</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{services.length}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Online</p>
            <p className="text-2xl font-bold text-emerald-500" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {services.filter(s => s.status === 'up').length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Offline</p>
            <p className="text-2xl font-bold text-red-500" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {services.filter(s => s.status === 'down').length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Alerts Enabled</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {services.filter(s => s.alert_enabled).length}
            </p>
          </div>
        </div>
      )}

      {/* Add Service Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-service-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Internal Service</DialogTitle>
            <DialogDescription>
              Monitor a server, application, or network device
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="service-name">Service Name</Label>
              <Input
                id="service-name"
                placeholder="e.g., Support Portal"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                className="mt-2"
                data-testid="service-name-input"
              />
            </div>

            <div>
              <Label htmlFor="service-host">Host (IP or Hostname)</Label>
              <Input
                id="service-host"
                placeholder="e.g., support.therapymgmt.com or 192.168.1.100"
                value={newService.host}
                onChange={(e) => setNewService({ ...newService, host: e.target.value })}
                className="mt-2"
                data-testid="service-host-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {newService.check_type !== 'ping' && (
                <div>
                  <Label htmlFor="service-port">Port</Label>
                  <Input
                    id="service-port"
                    type="number"
                    placeholder="443"
                    value={newService.port}
                    onChange={(e) => setNewService({ ...newService, port: e.target.value })}
                    className="mt-2"
                    data-testid="service-port-input"
                  />
                </div>
              )}
              <div className={newService.check_type === 'ping' ? 'col-span-2' : ''}>
                <Label htmlFor="check-type">Check Type</Label>
                <Select
                  value={newService.check_type}
                  onValueChange={(value) => setNewService({ ...newService, check_type: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="check-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tcp">TCP Connect</SelectItem>
                    <SelectItem value="https">HTTPS</SelectItem>
                    <SelectItem value="ping">Ping (ICMP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="alert-enabled"
                checked={newService.alert_enabled}
                onCheckedChange={(checked) => setNewService({ ...newService, alert_enabled: checked })}
                data-testid="alert-enabled-checkbox"
              />
              <Label htmlFor="alert-enabled" className="text-sm">
                Send email alert when service goes down
              </Label>
            </div>

            <div className="border-t border-border/50 pt-4 mt-4">
              <p className="text-sm font-medium mb-3">Contact Settings (when service is down)</p>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="contact-name">Contact Name/Group</Label>
                  <Input
                    id="contact-name"
                    placeholder="e.g., Area Manager and DOR group"
                    value={newService.contact_name}
                    onChange={(e) => setNewService({ ...newService, contact_name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contact-emails">Contact Emails (comma-separated)</Label>
                  <Input
                    id="contact-emails"
                    placeholder="e.g., dor@company.com, areamanager@company.com"
                    value={newService.contact_emails}
                    onChange={(e) => setNewService({ ...newService, contact_emails: e.target.value })}
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="down-message">Down Message</Label>
                  <Textarea
                    id="down-message"
                    placeholder="Message to display when service is down..."
                    value={newService.down_message}
                    onChange={(e) => setNewService({ ...newService, down_message: e.target.value })}
                    className="mt-2"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddService} data-testid="save-service-btn">
              Add Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Edit Service Contact Settings
            </DialogTitle>
            <DialogDescription>
              Configure who to contact and what message to display when {editingService?.name} is down
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-contact-name">Contact Name/Group</Label>
              <Input
                id="edit-contact-name"
                placeholder="e.g., Area Manager and DOR group"
                value={editingService?.contact_name || ''}
                onChange={(e) => setEditingService({ ...editingService, contact_name: e.target.value })}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Who should be contacted when this service is down?
              </p>
            </div>
            
            <div>
              <Label htmlFor="edit-contact-emails">Contact Emails (comma-separated)</Label>
              <Input
                id="edit-contact-emails"
                placeholder="e.g., dor@company.com, areamanager@company.com"
                value={editingService?.contact_emails || ''}
                onChange={(e) => setEditingService({ ...editingService, contact_emails: e.target.value })}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                These emails will be pre-filled in the &ldquo;Report Outage&rdquo; link
              </p>
            </div>
            
            <div>
              <Label htmlFor="edit-down-message">Down Message</Label>
              <Textarea
                id="edit-down-message"
                placeholder="Message to display when service is down..."
                value={editingService?.down_message || ''}
                onChange={(e) => setEditingService({ ...editingService, down_message: e.target.value })}
                className="mt-2"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This message will be shown to users when the service is unreachable
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditService}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
